<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: meta.links.show,
                    },
                    {
                        name: `${$tc('messages.questions', 2)} (${selectedQuestions.length})`,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="md:flex justify-between">
            <div>
                <aside class="justify-self-center">
                    <nav
                        class="inline-flex items-center justify-center p-1 space-x-1 overflow-hidden text-sm text-gray-600 bg-gray-100 rounded-xl"
                    >
                        <template v-for="link in meta.menu.links" :key="link.url">
                            <template v-if="link.selected">
                                <AppButton :href="link.url" element="inertia-link">
                                    {{ link.display_name }}
                                </AppButton>
                            </template>
                            <template v-else>
                                <InertiaLink
                                    :href="link.url"
                                    class="inline-flex items-center h-8 px-5 font-medium transition rounded-lg hover:text-gray-800 focus:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-inset"
                                >
                                    {{ link.display_name }}
                                </InertiaLink>
                            </template>
                        </template>
                    </nav>
                </aside>
            </div>
            <div>
                <ImportActivityQuestionsPanel :endpoint="importQuestionsEndpoint" :questionCount="questions.length" />
            </div>
        </div>

        <div class="mt-3 flex items-center space-x-2">
            <FormInput
                v-model.lazy.trim="search"
                :placeholder="`${$t('messages.search')}: ${$tc('messages.tags', 2)}`"
                max="191"
                type="text"
                class="inline-block"
            />

            <div class="rounded-md bg-blue-50 p-4">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <!-- Heroicon name: mini/information-circle -->
                        <svg
                            class="h-5 w-5 text-blue-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M19 10.5a8.5 8.5 0 11-17 0 8.5 8.5 0 0117 0zM8.25 9.75A.75.75 0 019 9h.253a1.75 1.75 0 011.709 2.13l-.46 2.066a.25.25 0 00.245.304H11a.75.75 0 010 1.5h-.253a1.75 1.75 0 01-1.709-2.13l.46-2.066a.25.25 0 00-.245-.304H9a.75.75 0 01-.75-.75zM10 7a1 1 0 100-2 1 1 0 000 2z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <div class="ml-3 flex-1 md:flex md:justify-between">
                        <p class="text-sm text-blue-700">
                            {{ $t('messages.instructions_activity_type_challenge') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <form :action="meta.links.edit" method="GET" @submit.prevent="filter">
            <div>
                <TransitionGroup
                    enter-active-class="transition-opacity ease-linear duration-300"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition-opacity ease-linear duration-300"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                    tag="div"
                    class="flex flex-wrap space-x-2 items-center mt-1"
                >
                    <label v-for="tag in filteredTags" :key="tag.name">
                        <input
                            :id="tag.id"
                            :value="tag.id"
                            type="checkbox"
                            v-model="form.tag_ids"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mt-1"
                        />
                        {{ tag.name }}
                    </label>
                </TransitionGroup>
            </div>

            <div class="flex justify-end">
                <div class="">
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <AlertMessage class="mt-5 inline-block" type="info" v-if="selectedQuestions.length < 10">
            <template v-slot:icon>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class=""
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <circle cx="12" cy="12" r="9"></circle>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    <polyline points="11 12 12 12 12 16 13 16"></polyline>
                </svg>
            </template>
            <template v-slot:title>
                {{ $t('messages.information') }}
            </template>
            <template v-slot:body>
                <span class="whitespace-pre-line">{{
                    $t('messages.select_at_least_X_questions', {
                        number: 10,
                    })
                }}</span>
            </template>
        </AlertMessage>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="table-fixed min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th scope="col" class="relative w-12 px-6 sm:w-16 sm:px-8 bg-gray-50">
                                    <SelectAllNoneButtons
                                        :max-count="questions.length"
                                        :selected-count="selectedQuestions.length"
                                        @select-all="selectAllQuestions()"
                                        @select-none="selectedQuestions = []"
                                        class="flex"
                                    />
                                </th>
                                <th
                                    class="w-10 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <div>{{ $t('messages.title') }}</div>
                                    {{ $t('messages.description') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.difficulty') }}
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="(question, sessionIndex) in questions"
                                :class="sessionIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
                                :key="sessionIndex"
                            >
                                <td class="relative w-12 px-6 sm:w-16 sm:px-8">
                                    <div
                                        v-if="selectedQuestions.includes(question.id)"
                                        class="absolute inset-y-0 left-0 w-0.5 bg-indigo-600"
                                    />
                                    <input
                                        type="checkbox"
                                        class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                                        :value="question.id"
                                        v-model="selectedQuestions"
                                    />
                                </td>
                                <td class="truncate px-6 py-4 text-sm leading-5 text-gray-900">
                                    <div class="flex space-x-2">
                                        <span>{{ question.title }}</span>
                                    </div>
                                    <div v-if="question.description">
                                        <QuestionDescriptionDisplay :question="question" />
                                    </div>
                                </td>
                                <td class="truncate px-6 py-4 text-sm leading-5 text-gray-900">
                                    <SmallNumberRangeIndicator :max="5" :value="question.difficulty" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import SmallNumberRangeIndicator from '@/components/SmallNumberRangeIndicator.vue';
import { debounce } from 'lodash';
import axios from 'axios';
import AlertMessage from '@/components/AlertMessage.vue';
import deburr from 'lodash/deburr.js';
import ImportActivityQuestionsPanel from '@/components/pageSpecific/ImportActivityQuestionsPanel.vue';
import QuestionDescriptionDisplay from '@/components/Quiz/QuestionDescriptionDisplay.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        QuestionDescriptionDisplay,
        PanelPage,
        AppButton,
        SmallNumberRangeIndicator,
        SelectAllNoneButtons,
        FormInput,
        AlertMessage,
        ImportActivityQuestionsPanel,
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'questions', 'activity', 'filters', 'tags', 'importQuestionsEndpoint'],
    data() {
        return {
            selectedQuestions: this.activity.question_ids,
            form: this.$inertia.form({
                tag_ids: this.filters.tag_ids.map((t) => +t),
            }),
            search: '',
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.edit);
        },
        selectAllQuestions() {
            this.selectedQuestions = this.questions.map((q) => q.id);
        },
        syncQuestions: debounce(function () {
            axios.patch(this.meta.links.update, { question_ids: this.selectedQuestions });
        }, 3000),
    },
    watch: {
        selectedQuestions() {
            this.syncQuestions();
        },
    },
    computed: {
        filteredTags() {
            if (this.search.length === 0) {
                return this.tags;
            }

            return this.tags.filter((t) => {
                if (this.form.tag_ids.includes(t.id)) {
                    return true;
                }
                let search = deburr(this.search.toUpperCase());
                if (this.search.length && deburr(t.name.toUpperCase()).includes(search)) {
                    return true;
                }
                return false;
            });
        },
    },
};
</script>
